import { useEffect, useState } from 'react';
import { Truck, Package, Globe, ShieldCheck, RefreshCcw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCMS } from '../hooks/useCMS';
import { apiFetch } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { trackPageView } from '../lib/analytics';

// 運送說明頁（CMS 可編；文案 key 前綴 shipping.page.*）
// 對應 RESEARCH 痛點：現站零運送資訊。運費表動態讀 shipping-zones 公開 API——
// 客人未啟用 zone 前（Groundwork 現狀）顯示「即將公佈」，啟用後自動變成真實價目表。

interface ZoneRate { id: number; label: string; baseCents: number; etaDays: string | null }
interface Zone { id: number; name: string; code: string; freeOverCents: number | null; rates: ZoneRate[] }

export default function Shipping() {
  const { getValue, getHtml } = useCMS();
  const [zones, setZones] = useState<Zone[] | null>(null);

  useEffect(() => {
    trackPageView('/shipping');
    apiFetch('/api/public/shipping/zones')
      .then((res) => res.json())
      .then((json) => setZones(Array.isArray(json) ? json : json?.data || []))
      .catch(() => setZones([]));
  }, []);

  const sections = [
    {
      icon: Package,
      titleKey: 'shipping.page.local_title',
      titleFallback: '香港本地配送',
      bodyKey: 'shipping.page.local_body',
      bodyFallback: '乾貨與凍貨本地配送由 YYC 倉庫直接出貨。偏遠地區（東涌／馬灣／愉景灣／赤臘角）或需另計費用，落單前歡迎查詢。',
    },
    {
      icon: Globe,
      titleKey: 'shipping.page.overseas_title',
      titleFallback: '海外包稅直郵',
      bodyKey: 'shipping.page.overseas_body',
      bodyFallback: '海外訂單經 YYC 小包包稅專線直郵——食品、液體等敏感貨可走，清關一條龍。海外專區商品全部常溫耐儲，專為國際包裹運輸而選。',
    },
    {
      icon: ShieldCheck,
      titleKey: 'shipping.page.customs_title',
      titleFallback: '清關與稅項',
      bodyKey: 'shipping.page.customs_body',
      bodyFallback: '專線已包含目的地清關及基本稅項（包稅）。個別國家對肉類製品的進口限制不同，請參閱各商品頁的可寄目的地標示。',
    },
    {
      icon: RefreshCcw,
      titleKey: 'shipping.page.returns_title',
      titleFallback: '退換貨安排',
      bodyKey: 'shipping.page.returns_body',
      bodyFallback: '食品類商品如非質量問題恕不退換。如收到貨品有損毀或質量問題，請於收貨後 48 小時內連同照片聯絡我們跟進。',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white py-16 md:py-20 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(120% 140% at 15% 0%, #1a6b50 0%, #12503c 38%, #0b3527 100%)' }}
        />
        <div className="absolute inset-0 bg-x1493-dark/40" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <Truck size={40} className="text-x1493-secondary mx-auto mb-4" />
          <h1
            className="text-3xl md:text-5xl font-bold mb-4"
            dangerouslySetInnerHTML={getHtml('shipping.page.title', '運送與配送說明')}
          />
          <p
            className="text-white/80 max-w-2xl mx-auto leading-relaxed"
            dangerouslySetInnerHTML={getHtml('shipping.page.subtitle', '由香港荃灣直達你手上——本地凍鏈、海外包稅，一條龍搞掂。')}
          />
        </div>
      </section>

      {/* 運費表（shipping-zones 動態） */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-2 text-stone-800">{getValue('shipping.page.rates_title', '運費及時效表')}</h2>
          <p className="text-sm text-stone-500 mb-6">{getValue('shipping.page.rates_note', '以下為現行收費；個別商品或因重量／體積另計，落單前以購物車運費試算為準。')}</p>

          {zones === null ? (
            <div className="text-center py-8 text-stone-400">載入中...</div>
          ) : zones.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center">
              <p className="font-bold text-stone-700 mb-2">{getValue('shipping.page.rates_pending_title', '運費價目即將公佈')}</p>
              <p className="text-sm text-stone-500">{getValue('shipping.page.rates_pending_body', '各目的地運費及時效正在最後確認中。落單流程已可正常使用，運費確認後我們會即時更新。')}</p>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-stone-600 text-left">
                  <tr>
                    <th className="px-4 py-3">目的地</th>
                    <th className="px-4 py-3">線種</th>
                    <th className="px-4 py-3">運費</th>
                    <th className="px-4 py-3">時效</th>
                    <th className="px-4 py-3">滿額免運</th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map((z) => z.rates.map((r, i) => (
                    <tr key={r.id} className={i === 0 ? 'border-t' : 'border-t border-stone-100'}>
                      {i === 0 && <td className="px-4 py-3 font-bold" rowSpan={z.rates.length}>{z.name}（{z.code}）</td>}
                      <td className="px-4 py-3">{r.label}</td>
                      <td className="px-4 py-3 font-bold text-x1493-primary">{formatPrice(r.baseCents)}{r.etaDays ? '' : ''}</td>
                      <td className="px-4 py-3">{r.etaDays ? `約 ${r.etaDays} 日` : '—'}</td>
                      <td className="px-4 py-3">{z.freeOverCents != null ? `滿 ${formatPrice(z.freeOverCents)} 免運` : '—'}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-stone-400 mt-4">{getValue('shipping.page.rates_footer', '* 運費以落單時系統計算為準；重量附加費（如有）會於確認訂單時列明。')}</p>
        </div>
      </section>

      {/* 四段說明 */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          {sections.map(({ icon: Icon, titleKey, titleFallback, bodyKey, bodyFallback }) => (
            <div key={titleKey} className="bg-white border border-stone-200 rounded-2xl p-6">
              <div className="w-12 h-12 bg-x1493-primary/10 rounded-2xl flex items-center justify-center mb-4">
                <Icon className="text-x1493-primary" size={24} />
              </div>
              <h3 className="font-bold text-lg mb-2 text-stone-800">{getValue(titleKey, titleFallback)}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{getValue(bodyKey, bodyFallback)}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
